import React, { useMemo, useContext } from 'react';
import { FormGroup, Input, Label } from 'src/components/ui';

import useUID from '../../util/useUID';
import Notification from '../Notification';
import Loader from '../Loader';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import useOptionsLookup from './hooks/useOptionsLookup';
import { Trans } from '@lingui/react/macro';
import { useJnx } from '../../util/jnx';
import { AuthContext } from '../../context/AuthContext';
import useTestId, { getTestId } from '../../hooks/useTestId';

function LookupMultiSelectCheckboxesFormField(props) {
  const {
    formData,
    registry,
    schema: { title, lookup, items: { type } = {}, readOnly },
    required,
    onChange: propOnChange,
    fieldPathId: { $id, path: fieldPath },
  } = props;
  const auth = useContext(AuthContext);
  const formContext = registry?.formContext;
  const { sideChannel } = formContext;

  const { resource = 'Options', id, label, labelExpr } = lookup || {};
  const isNumber = type === 'number';

  const labelJnx = useJnx(labelExpr || null);

  const uid = useUID();
  const testIdProps = useTestId($id);

  const rootFormData = useSideChannelSubscription(sideChannel, 0);
  const dataPath = useMemo(() => getPathFromId($id), [$id]);

  const { options: lookupOptions, loadingOptions, errorLoadingOptions } = useOptionsLookup({
    lookup,
    rootFormData,
    path: dataPath,
    auth,
  });

  const options = useMemo(
    () =>
      (lookupOptions || []).map(item => {
        const raw = item[id];
        const value = isNumber ? (Number.isNaN(Number(raw)) ? raw : Number(raw)) : raw;
        const _label = item && labelJnx ? labelJnx.eval(item) : item?.[label] ?? '';
        return { value, label: _label };
      }),
    [lookupOptions, id, label, isNumber, labelJnx]
  );

  function isChecked(optionValue) {
    return !!formData && Array.isArray(formData) && formData.indexOf(optionValue) !== -1;
  }

  function setChecked(optionValue, checked) {
    const newFormData = !!formData && Array.isArray(formData) ? formData.slice() : [];
    const dataIndex = newFormData.indexOf(optionValue);

    if (checked && dataIndex === -1) {
      newFormData.push(optionValue);
    } else if (!checked && dataIndex !== -1) {
      newFormData.splice(dataIndex, 1);
    }

    const filtered = newFormData.filter(element => element !== undefined);
    propOnChange(filtered.length > 0 ? filtered : [], fieldPath);
  }

  return (
    <div className="form-group field field-number" {...testIdProps}>
      <label className="control-label">
        {title}
        {required ? <span className="required">*</span> : null}
      </label>
      {loadingOptions ? (
        <div>
          <Loader dots inline>
            <Trans>Loading</Trans> {resource}
          </Loader>
        </div>
      ) : errorLoadingOptions ? (
        <Notification color="danger">
          <div>{errorLoadingOptions.message}</div>
        </Notification>
      ) : (options || []).length > 0 ? (
        (options || []).map((option, idx) => (
          <FormGroup key={idx} check className="flex align-center">
            <Input
              disabled={readOnly}
              id={`${uid}-${idx}`}
              {...getTestId($id, option.value)}
              type="checkbox"
              checked={isChecked(option.value)}
              onChange={({ target: { checked } }) => setChecked(option.value, checked)}
              className="my-2"
            />
            <Label check for={`${uid}-${idx}`} className="mb-0">
              {option.label}
            </Label>
          </FormGroup>
        ))
      ) : (
        <Notification color="danger">
          <div>{resource} list is empty.</div>
        </Notification>
      )}
    </div>
  );
}

export default LookupMultiSelectCheckboxesFormField;
